import { useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { sortTypes, TSortTypes } from 'pages/ImageManagement/contants';
import {
    TPictures,
    useDeletePicturesMutation,
    useGetAllPicturesQuery,
    useUpdatePicturesMutation,
} from 'core/API/picturesApi';
import { useTranslation } from 'helpers/languages/translations';
import { usePicturesFilters } from '../ImagesUpload/ImageUploadContainer';
import useGetFilteredPictures, { getImageByFilters } from './useGetFilteredPictures';
import { switchFilter } from './ToolBar';
import { useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';

const formatList = (
    imageList: TPictures[][],
    checkedPictures: string[],
    isGrouped: boolean,
    setCheckedPicture: (val: string[]) => void,
) =>
    imageList.flatMap((img, idx) => {
        if (isGrouped) {
            const nbChecked = img.filter((i) => i._id && checkedPictures.includes(i._id)).length;
            const isAllChecked = nbChecked === img.length;

            return {
                key: `${img[0]._id}`,
                picture: img[0],
                nbPictures: img.length,
                pictureGroup: img,
                isChecked: isAllChecked,
                isIndeterminate: !isAllChecked && !!nbChecked,
                onCheck: setCheckedPicture,
            };
        }

        return img.flatMap((picture) => ({
            key: `${picture._id}`,
            picture: picture,
            pictureGroup: img,
            isChecked: picture._id && !!checkedPictures.includes(picture._id),
            onCheck: setCheckedPicture,
        }));
    });

export const useArchiveOrDelete = () => {
    const toast = useToast();
    const translate = useTranslation();
    const [deletePicturesQuery] = useDeletePicturesMutation();
    const [updatePicturesQuery] = useUpdatePicturesMutation();

    return async (action: 'delete' | 'archive' | 'unarchive', checkedPictures: string[]) => {
        let resp: any;

        if (/archive/.test(action)) {
            resp = await updatePicturesQuery({
                pictureIds: checkedPictures,
                updates: { status: action === 'archive' ? 'archived' : 'active' },
            });
        } else {
            resp = await deletePicturesQuery({ ids: checkedPictures, isDelete: action === 'delete' });
        }

        const isError = 'error' in resp;

        toast({
            title: translate(isError ? 'error.generic' : `pictures.${action}.success`),
            status: isError ? 'error' : 'success',
        });
    };
};

const EMPTY_PICTURES: TPictures[] = [];

const useImagesViewer = ({ readonly }: { readonly: boolean }) => {
    const { data: pictures, isFetching } = useGetAllPicturesQuery({});

    // Must stay referentially stable: `allPictures` is an effect dependency in
    // `useGetFilteredPictures`, so a fresh `[]` per render causes an update loop.
    const allPictures = pictures?.data ?? EMPTY_PICTURES;

    const [choosenSort, setChoosenSort] = useState<TSortTypes>(Object.values(sortTypes)[0]);
    const [isAscendingOrder, setIsAscendingOrder] = useState(false);
    const [inputFilter, setInputFilter] = useState('');
    const [isGrouped, setIsGrouped] = useState(true);
    const [checkedPictures, setCheckedPictures] = useState<string[]>([]);

    const { filters, image_caractersitics, image_type, duplicatePosition, isFromApi, setFilters } = usePicturesFilters(
        switchFilter.active,
        true,
    );

    const filtersList = { image_caractersitics, image_type, isFromApi, duplicatePosition };

    const imageProps = {
        allPictures,
        choosenSort,
        isAscendingOrder,
        inputFilter,
        filters,
    };

    const { imageList } = useGetFilteredPictures(imageProps);

    const imageListActive = getImageByFilters(allPictures, { filters: switchFilter.active }, true);
    const imageListArchived = getImageByFilters(allPictures, { filters: switchFilter.archived }, true);
    const imageListOther = getImageByFilters(allPictures, { filters: switchFilter.other }, true);

    const setCheckedPicture = useCallback(
        (pictureIds: string[], all?: boolean) => {
            if (all) {
                setCheckedPictures(imageList.flatMap((img) => img.flatMap((i) => i._id as string)));
            } else if (all === false) {
                setCheckedPictures([]);
            } else {
                setCheckedPictures((picturesChecked) => {
                    let newPicturesChecked = [...picturesChecked];

                    // if ID is found, we need to uncheck
                    const isUncheck = newPicturesChecked.includes(pictureIds[0]);

                    if (isUncheck) {
                        newPicturesChecked = newPicturesChecked.filter((pc) => !pictureIds.includes(pc));
                    } else {
                        newPicturesChecked.push(...pictureIds);
                    }

                    return newPicturesChecked;
                });
            }
        },
        [imageList],
    );

    const nbPictures = isGrouped ? imageList.length : imageList.flat().length;

    const toggleAscending = useCallback(() => {
        setIsAscendingOrder((order) => !order);
    }, []);

    const formatedImageList = formatList(imageList, checkedPictures, isGrouped, setCheckedPicture);

    return {
        isFetching,
        imageList: formatedImageList,
        imageListLength: imageList.length,
        isGrouped,
        choosenSort,
        isAscendingOrder,
        nbPictures: {
            current: nbPictures,
            active: imageListActive,
            archived: imageListArchived,
            other: imageListOther,
        },
        inputFilter,
        filtersList,
        checkedPictures,
        readonly,
        setCheckedPicture,
        setChoosenSort,
        toggleAscending,
        setIsGrouped,
        setInputFilter,
        setFilters,
    };
};

export type TImagesViewer = ReturnType<typeof useImagesViewer>;
export default useImagesViewer;
